import { useCallback } from "react";
import { AssistedServiceBarView } from "~/features/assistedService/components/AssistedServiceBar/AssistedServiceBarView";
import { useAssistedService } from "~/features/assistedService/hooks/useAssistedService";
import { searchCustomerSuggestions } from "~/features/assistedService/api/asmAswsApi";

/**
 * Connected wrapper for the ASM bar. Wires {@link useAssistedService}
 * (token-state session, PKCE login, F50 logout, timer, collapse cookie) into
 * the prop-driven {@link AssistedServiceBarView}. Shell-owned: mounted
 * unconditionally by {@link StorefrontShellFrame}; self-gates and renders
 * nothing while an ASM PKCE callback completes or when ASM is not launched
 * (no URL launch marker and no agent token).
 */
export const AssistedServiceBar = () => {
  const vm = useAssistedService();

  const searchCustomers = useCallback(
    /**
     * Customer autocomplete via the ASWS suggestions route (task 0250: replaces
     * the legacy storefront `GET /assisted-service/autocomplete`; the ASWS GET
     * twin is deprecated and must never be used).
     */
    async (customerId: string, signal?: AbortSignal) => searchCustomerSuggestions(customerId, signal),
    [],
  );

  // Self-gate: render nothing while a callback completes or when ASM is inactive.
  if (vm.isLoading) {
    return null;
  }
  if (!vm.state.launched && !vm.state.agent) {
    return null;
  }

  return (
    <AssistedServiceBarView
      state={vm.state}
      remainingSeconds={vm.remainingSeconds}
      collapsed={vm.collapsed}
      alert={vm.alert}
      loginPending={vm.loginPending}
      personifyPending={vm.personifyPending}
      onLogin={vm.login}
      onPersonify={vm.personify}
      onPersonifyStop={vm.personifyStop}
      onLogout={vm.logout}
      onClose={vm.closeAssistedService}
      onResetTimer={vm.resetTimer}
      onToggleCollapse={vm.toggleCollapsed}
      onSearchCustomers={searchCustomers}
    />
  );
};
